// src/components/InfiniteList.tsx
"use client";

import { ReactNode, useCallback, useState } from "react";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useTranslations } from "next-intl";

type InfiniteListProps<T> = {
  initialItems: T[];
  initialPage: number;
  initialHasMore: boolean;
  getNextPage: (page: number) => Promise<{ items: T[]; hasMore: boolean }>;
  children: (items: T[]) => ReactNode;
};

export default function InfiniteList<T>({
  initialItems,
  initialPage,
  initialHasMore,
  getNextPage,
  children,
}: InfiniteListProps<T>) {
  const [items, setItems] = useState<T[]>(initialItems);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations("label");

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const nextPage = page + 1;
      const { items: newItems, hasMore: newHasMore } =
        await getNextPage(nextPage);

      setItems((prev) => [...prev, ...newItems]);
      setPage(nextPage);
      setHasMore(newHasMore);
    } finally {
      setIsLoading(false);
    }
  }, [page, hasMore, isLoading, getNextPage]);

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: loadMore,
  });

  return (
    <>
      {children(items)}

      <div ref={sentinelRef} style={{ height: 1 }} />

      {isLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "1.5rem 0",
            fontSize: "0.875rem",
            opacity: 0.7,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <span className="spinner" aria-hidden="true" />
          {t('loading_more')}
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "1.5rem 0",
            fontSize: "0.8rem",
            opacity: 0.45,
          }}
        >
          {t('end_of_list')}
        </div>
      )}
    </>
  );
}